import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { STEP_TYPE_STATUSES, STEP_TYPES } from 'constants/status.constants';
import { ComputedFrom } from '@certemy/common';
import { FeesScreens } from '../fees-details.modal.component';
import { FeesStepInstance } from 'services/professional/step/step.interface';
import { isPendingVerificationStatus } from 'utils/step.helpers';
import moment from 'moment-mini';
import {
  calculateFeeFormGrandTotal,
  calculateStepFee,
  formShouldButIsNotFilled,
  hasGracePeriodCome,
} from 'components/organization-certification/step/fee/fee-step.constants';
import { getFullFeeStepAmount } from 'components/organization-certification/step/fee/fee-step.constants';
import _get from 'lodash-es/get';
import _size from 'lodash-es/size';
import { MessageQueryService, SmartForm } from '@certemy/ui-components';
import { FeatureToggleService, FEATURES } from 'features/feature-toggle';
import { PaymentFormService } from 'features/payment-form';
import { take, filter } from 'rxjs/operators';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { Store } from '@ngrx/store';

const PAID_BY = {
  ITSELF: 1,
  THIRD_PARTY: 2,
};

const PAYMENT_TYPES = {
  VOUCHER: 0,
  CREDIT_CARD: 1,
};

const VOUCHER_VALIDATOR_REGEX = /^[A-Z]{4}-\d{3}-[A-Z]{3}$/;

@Component({
  selector: 'cm-fees-initial-screen',
  templateUrl: './fees-initial-screen.component.html',
  styleUrls: ['./fees-initial-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeesInitialScreenComponent implements OnInit {
  @Input() step: FeesStepInstance;
  @Output() switchScreen = new EventEmitter();
  @Output() closeModal = new EventEmitter();
  @Output() dismissModal = new EventEmitter();
  @Output() toggleMode = new EventEmitter();
  @Output() changeVoucherValue = new EventEmitter();
  @Input() voucherValue: string;
  @Input() isPreviousPaymentPage: boolean;
  @Input() workflowTypeId: number;
  readonly WORKFLOW_MODULE_ID = WORKFLOW_MODULE_ID;

  readonly STEP_STATUSES = STEP_TYPE_STATUSES;
  readonly FeesScreens = FeesScreens;
  readonly PAYMENT_TYPES = PAYMENT_TYPES;
  readonly FEATURES = FEATURES;
  readonly STEP_TYPES = STEP_TYPES;
  private COMPACT_PAGES = [FeesScreens.ThirdParty, FeesScreens.ResetConfirm];
  payments: SmartForm.FieldOption[] = [
    {
      id: PAYMENT_TYPES.VOUCHER,
      name: 'Voucher',
      tooltip: 'The voucher is a unique code that can be used as a credit for purchases',
    },
    {
      id: PAYMENT_TYPES.CREDIT_CARD,
      name: 'Credit card',
    },
  ];
  selectedPayment = PAYMENT_TYPES.CREDIT_CARD;
  showVoucherFormatError = false;
  hasFeatureVouchers = false;
  feeFormWithVoucher = false;
  currencyIcon$ = this.store.select(ProfileSelectors.paymentSymbol);

  constructor(
    private messageQueryService: MessageQueryService,
    private featureToggleService: FeatureToggleService,
    private paymentService: PaymentFormService,
    private store: Store<any>,
  ) {}

  @ComputedFrom('step')
  get daysRemainingToGracePeriod(): number {
    return moment(this.step.end_date).add(this.step.content.gracePeriod, 'day').add(1, 'day').diff(moment(), 'days');
  }

  @ComputedFrom('step')
  get hasGracePeriodCome(): boolean {
    return hasGracePeriodCome(this.step.end_date, this.step.content.gracePeriod);
  }

  @ComputedFrom('step')
  get isCompletionAvailable() {
    return (
      this.step.status_id === this.STEP_STATUSES.INCOMPLETE ||
      this.step.status_id === this.STEP_STATUSES.PASSED_DUE_DATE
    );
  }

  @ComputedFrom('step')
  get isPendingVerificationStatus() {
    return isPendingVerificationStatus(this.step.status_id);
  }

  @ComputedFrom('step')
  get hasFeeForm(): boolean {
    return !!_size(this.step.content.feesFormFields);
  }

  @ComputedFrom('step')
  get paidByVoucher(): boolean {
    return this.step.status_id === this.STEP_STATUSES.COMPLETED && this.step.content.isPaidByVoucher;
  }

  @ComputedFrom('step')
  get paidByThirdParty(): boolean {
    return this.step.content.paid_by === PAID_BY.THIRD_PARTY;
  }

  @ComputedFrom('step')
  get documents() {
    return this.step.content.documents.map((file) => {
      return {
        ...file,
        url: file.link,
      };
    });
  }

  @ComputedFrom('step')
  get stepFee(): number {
    const { isLateFeeEnabled, fee, lateFee, gracePeriod, paidAmount } = this.step.content;
    const formSum = _get(this.step.content.feesFormFields, 'length')
      ? calculateFeeFormGrandTotal(this.step.content.feesFormFields)
      : 0;

    return paidAmount
      ? paidAmount - formSum
      : calculateStepFee({
          stepEndDate: this.step.end_date,
          isLateFeeEnabled,
          fee,
          lateFee,
          gracePeriod,
        });
  }

  @ComputedFrom('step', 'paidByThirdParty', 'hasFeatureVouchers', 'isPendingVerificationStatus')
  get isPaymentTypeVisible(): boolean {
    if (this.step.status_id !== this.STEP_STATUSES.COMPLETED) {
      return this.paymentAmount && this.hasFeatureVouchers && !this.isPendingVerificationStatus;
    }
    return false;
  }

  get formShouldButNotFilled(): boolean {
    return formShouldButIsNotFilled(this.step.content.feesFormFields, this.step.content.fee);
  }

  get paymentAmount(): number {
    return getFullFeeStepAmount(this.step);
  }

  ngOnInit() {
    this.featureToggleService
      .hasFeatures(FEATURES.VOUCHERS)
      .pipe(
        take(1),
        filter((hasFeature) => hasFeature),
      )
      .subscribe(() => {
        this.hasFeatureVouchers = this.step.content.isPayableByVoucher;
        this.feeFormWithVoucher = this.hasFeatureVouchers && this.isPreviousPaymentPage && this.hasFeeForm;
        this.selectedPayment =
          this.voucherValue || this.paidByVoucher ? PAYMENT_TYPES.VOUCHER : PAYMENT_TYPES.CREDIT_CARD;
      });
  }

  private validateVoucher(voucherProperty: string) {
    const errorMessage = 'Please, provide correct data in requested fields.';
    const voucherValue = voucherProperty ? voucherProperty.trim() : voucherProperty;
    if (!voucherValue || !VOUCHER_VALIDATOR_REGEX.test(voucherValue)) {
      this.showVoucherFormatError = true;
      this.messageQueryService.addErrorMessage(errorMessage);
      return;
    }

    this.paymentService.validateVoucher({ voucherValue, certification_id: this.step.certification_id }).subscribe(
      () => {
        this.changeVoucherValue.emit(voucherValue);
      },
      (err) => {
        if (!err.handled) {
          this.messageQueryService.addErrorMessage(err.showIfNotHandled);
        }
      },
    );
  }

  private handlePayment() {
    if (this.hasFeeForm && this.hasFeatureVouchers && !this.feeFormWithVoucher) {
      this.feeFormWithVoucher = true;
      return;
    }
    if (this.selectedPayment === PAYMENT_TYPES.VOUCHER) {
      this.validateVoucher(this.voucherValue);
      return;
    }
    this.switchScreen.emit(FeesScreens.Payment);
  }

  goToPage(page) {
    if (page === FeesScreens.Payment) {
      this.handlePayment();
      return;
    }

    if (this.COMPACT_PAGES.includes(page)) {
      this.toggleMode.emit();
    }
    this.switchScreen.emit(page);
  }

  backToForm() {
    this.feeFormWithVoucher = false;
  }

  changePaymentType(paymentId) {
    if (paymentId === PAYMENT_TYPES.CREDIT_CARD) {
      this.changeVoucherValue.emit(null);
    }
    this.selectedPayment = paymentId;
  }

  feeFormFieldsChange(fields) {
    this.step.content.feesFormFields = fields;
  }
}
